/**
 * Procurement API Service
 * All API calls for Procurement Module
 * Follows the frontend specification contract
 */

import api from "../../../services/api";

const BASE_PATH = "/api/v1/procurement";

// ==================== PROCUREMENT REQUESTS ====================

/**
 * Get all procurement requests
 * GET /api/v1/procurement/requests
 */
export const getRequests = async (params = {}) => {
  const response = await api.get(`${BASE_PATH}/requests`, { params });
  return response.data;
};

/**
 * Get procurement request by ID
 * GET /api/v1/procurement/requests/:id
 */
export const getRequestById = async (id) => {
  const response = await api.get(`${BASE_PATH}/requests/${id}`);
  return response.data;
};

/**
 * Create a new procurement request
 * POST /api/v1/procurement/requests
 */
export const createRequest = async (data) => {
  const response = await api.post(`${BASE_PATH}/requests`, data);
  return response.data;
};

/**
 * Update an existing procurement request
 * PUT /api/v1/procurement/requests/:id
 */
export const updateRequest = async (id, data) => {
  const response = await api.put(`${BASE_PATH}/requests/${id}`, data);
  return response.data;
};

/**
 * Restart approval with a new workflow version
 * POST /api/v1/procurement/requests/:id/restart-approval
 */
export const restartApprovalWithWorkflow = async (id, data) => {
  const response = await api.post(
    `${BASE_PATH}/requests/${id}/restart-approval`,
    data,
  );
  return response.data;
};

/**
 * Delete a procurement request (soft delete)
 * DELETE /api/v1/procurement/requests/:id
 */
export const deleteRequest = async (id) => {
  const response = await api.delete(`${BASE_PATH}/requests/${id}`);
  return response.data;
};

/**
 * Submit a procurement request for approval
 * POST /api/v1/procurement/requests/:id/submit
 */
export const submitRequest = async (id) => {
  const response = await api.post(`${BASE_PATH}/requests/${id}/submit`);
  return response.data;
};

/**
 * Approve a procurement request
 * POST /api/v1/procurement/requests/:id/approve
 */
export const approveRequest = async (id, data) => {
  const response = await api.post(`${BASE_PATH}/requests/${id}/approve`, data);
  return response.data;
};

/**
 * Reject a procurement request
 * POST /api/v1/procurement/requests/:id/reject
 */
export const rejectRequest = async (id, data) => {
  const response = await api.post(`${BASE_PATH}/requests/${id}/reject`, data);
  return response.data;
};

/**
 * Send back a procurement request for revision
 * POST /api/v1/procurement/requests/:id/send-back
 */
export const sendBackRequest = async (id, data) => {
  const response = await api.post(
    `${BASE_PATH}/requests/${id}/send-back`,
    data,
  );
  return response.data;
};

/**
 * Approve by bench — override all remaining workflow stages
 * POST /api/v1/procurement/requests/:id/approve-bench
 */
export const approveBench = async (id, data) => {
  const response = await api.post(
    `${BASE_PATH}/requests/${id}/approve-bench`,
    data,
  );
  return response.data;
};

/**
 * Bulk create procurement requests
 * POST /api/v1/procurement/requests/bulk
 */
export const bulkCreateRequests = async (requests) => {
  const response = await api.post(`${BASE_PATH}/requests/bulk`, { requests });
  return response.data;
};

/**
 * Get pending approvals for current user
 * GET /api/v1/procurement/requests/pending-approvals
 */
export const getPendingApprovals = async () => {
  const response = await api.get(`${BASE_PATH}/requests/pending-approvals`);
  return response.data;
};

/**
 * Perform approval action (approve/reject/send back)
 * POST /api/v1/procurement/requests/:id/action
 */
export const performAction = async (id, data) => {
  const response = await api.post(`${BASE_PATH}/requests/${id}/action`, data);
  return response.data;
};

// ==================== WORKFLOWS ====================

/**
 * Get all workflows
 * GET /api/v1/procurement/workflows
 */
export const getWorkflows = async () => {
  const response = await api.get(`${BASE_PATH}/workflows`);
  return response.data;
};

/**
 * Get active workflow
 * GET /api/v1/procurement/workflows/active
 */
export const getActiveWorkflow = async () => {
  const response = await api.get(`${BASE_PATH}/workflows/active`);
  return response.data;
};

/**
 * Get business unit constants for dropdowns
 * GET /api/v1/procurement/workflows/business-units
 */
export const getBusinessUnits = async () => {
  const response = await api.get(`${BASE_PATH}/workflows/business-units`);
  return response.data;
};

/**
 * Resolve workflow based on unit, department, branch
 * POST /api/v1/procurement/workflows/resolve
 */
export const resolveWorkflow = async (unit, department, branch) => {
  const response = await api.post(`${BASE_PATH}/workflows/resolve`, {
    unit,
    department,
    branch,
  });
  return response.data;
};

/**
 * Get workflow versions by unit/department/branch
 * GET /api/v1/procurement/workflows/versions
 */
export const getWorkflowVersions = async (unit, department, branch) => {
  const response = await api.get(`${BASE_PATH}/workflows/versions`, {
    params: { unit, department, branch },
  });
  return response.data;
};

/**
 * Get workflow by ID
 * GET /api/v1/procurement/workflows/:id
 */
export const getWorkflowById = async (id) => {
  const response = await api.get(`${BASE_PATH}/workflows/${id}`);
  return response.data;
};

/**
 * Create a new workflow
 * POST /api/v1/procurement/workflows
 */
export const createWorkflow = async (data) => {
  const response = await api.post(`${BASE_PATH}/workflows`, data);
  return response.data;
};

/**
 * Update an existing workflow
 * PUT /api/v1/procurement/workflows/:id
 */
export const updateWorkflow = async (id, data) => {
  const response = await api.put(`${BASE_PATH}/workflows/${id}`, data);
  return response.data;
};

/**
 * Activate a workflow
 * POST /api/v1/procurement/workflows/:id/activate
 */
export const activateWorkflow = async (id) => {
  const response = await api.post(`${BASE_PATH}/workflows/${id}/activate`);
  return response.data;
};

/**
 * Deactivate a workflow
 * POST /api/v1/procurement/workflows/:id/deactivate
 */
export const deactivateWorkflow = async (id) => {
  const response = await api.post(`${BASE_PATH}/workflows/${id}/deactivate`);
  return response.data;
};

// ==================== COMMENTS ====================

/**
 * List comments for a request
 * GET /api/v1/procurement/requests/:requestId/comments
 */
export const listComments = async (requestId) => {
  const response = await api.get(`${BASE_PATH}/requests/${requestId}/comments`);
  return response.data;
};

/**
 * Add a comment to a request
 * POST /api/v1/procurement/requests/:requestId/comments
 */
export const addComment = async (requestId, payload) => {
  const response = await api.post(
    `${BASE_PATH}/requests/${requestId}/comments`,
    payload,
  );
  return response.data;
};

/**
 * Upload attachments for a procurement comment draft
 * POST /api/v1/procurement/requests/:requestId/comments/upload-attachments
 */
export const uploadCommentAttachments = async (requestId, files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });

  const response = await api.post(
    `${BASE_PATH}/requests/${requestId}/comments/upload-attachments`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
};

/**
 * Edit a comment
 * PUT /api/v1/procurement/requests/:requestId/comments/:commentId
 */
export const editComment = async (requestId, commentId, payload) => {
  const response = await api.put(
    `${BASE_PATH}/requests/${requestId}/comments/${commentId}`,
    payload,
  );
  return response.data;
};

/**
 * Delete a comment (soft delete)
 * DELETE /api/v1/procurement/requests/:requestId/comments/:commentId
 */
export const deleteComment = async (requestId, commentId) => {
  const response = await api.delete(
    `${BASE_PATH}/requests/${requestId}/comments/${commentId}`,
  );
  return response.data;
};

// Export all functions as named exports
export default {
  // Requests
  getRequests,
  getRequestById,
  createRequest,
  updateRequest,
  restartApprovalWithWorkflow,
  deleteRequest,
  submitRequest,
  approveRequest,
  rejectRequest,
  sendBackRequest,
  approveBench,
  bulkCreateRequests,
  getPendingApprovals,
  performAction,

  // Workflows
  getWorkflows,
  getActiveWorkflow,
  getBusinessUnits,
  resolveWorkflow,
  getWorkflowVersions,
  getWorkflowById,
  createWorkflow,
  updateWorkflow,
  activateWorkflow,
  deactivateWorkflow,

  // Comments
  listComments,
  addComment,
  uploadCommentAttachments,
  editComment,
  deleteComment,
};
